import { Button } from '@ophelos/ui/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ophelos/ui/components/tooltip';
import { Eye, EyeOff } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useAIChatSafe } from '@/contexts/AIChatContext';

/**
 * Toggles the side panel that shows which scope a settings page edits and how it inherits.
 * Rendered as a span with role="button" rather than a <button>: SettingsLayout wraps
 * read-only pages in <fieldset disabled>, which disables every form control inside, and
 * the preview matters most on exactly those pages.
 */
export function ScopePreviewButton() {
  const chat = useAIChatSafe();
  if (!chat) return null;
  const { isOpen, resolvedOpenSource, open, close } = chat;
  const showing = isOpen && resolvedOpenSource === 'settings';
  const label = `${showing ? 'Hide' : 'Show'} settings hierarchy`;
  const toggle = () => (showing ? close() : open({ source: 'settings' }));
  const onKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="text-muted-foreground cursor-pointer" asChild>
          {/* biome-ignore lint/a11y/useSemanticElements: a <button> would be disabled by the read-only fieldset */}
          <span role="button" tabIndex={0} aria-pressed={showing} aria-label={label} onClick={toggle} onKeyDown={onKeyDown}>
            {showing ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
